import {
  Children,
  isValidElement,
  ReactElement,
  ReactNode,
  ComponentType,
} from "react";

import {
  TableHeader,
  TableColumn as TableColumnComp,
  TableBody,
  TableRow as TableRowComp,
  TableCell as TableCellComp,
  type TableHeaderProps,
  type TableColumnProps,
  type TableBodyProps,
  type TableRowProps,
  type TableCellProps,
} from "./CompoundComponents";
import type {
  TableColumnData,
  TableData,
  TableRowData,
  TableCellData,
} from "../types";

function isComponentType(
  element: ReactElement,
  componentType: ComponentType<any>
): boolean {
  return element.type === componentType;
}

/**
 * Parses JSX table structure into collection data format
 */
export function parseTableChildren(children: ReactNode): TableData {
  const columns: TableColumnData[] = [];
  const rows: TableRowData[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;

    // Parse columns from header
    if (isComponentType(child, TableHeader)) {
      const headerProps = child.props as TableHeaderProps;

      Children.forEach(headerProps.children, (columnChild) => {
        if (
          isValidElement(columnChild) &&
          isComponentType(columnChild, TableColumnComp)
        ) {
          const columnProps = columnChild.props as TableColumnProps;
          columns.push({
            key: columnProps.id,
            header: columnProps.children,
            isHidden: headerProps.isHidden || false,
            ariaSort: columnProps["aria-sort"],
          });
        }
      });
    }

    // Parse rows from body
    if (isComponentType(child, TableBody)) {
      const bodyProps = child.props as TableBodyProps;
      Children.forEach(bodyProps.children, (rowChild) => {
        if (
          isValidElement(rowChild) &&
          isComponentType(rowChild, TableRowComp)
        ) {
          const rowProps = rowChild.props as TableRowProps;
          const cells: TableCellData[] = [];

          let cellIndex = 0;
          Children.forEach(rowProps.children, (cellChild) => {
            if (
              isValidElement(cellChild) &&
              isComponentType(cellChild, TableCellComp)
            ) {
              const cellProps = cellChild.props as TableCellProps;

              // Map cell to column by index if no explicit columnKey
              const columnKey = cellProps.columnKey || columns[cellIndex]?.key;

              if (columnKey) {
                cells.push({
                  columnKey,
                  value: cellProps.children,
                  colSpan: cellProps.colSpan,
                });
              }
              cellIndex++;
            }
          });

          rows.push({
            key:
              (rowChild.key as string) || rowProps.key || String(rows.length),
            cells,
            onClick: rowProps.onClick,
          });
        }
      });
    }
  });

  return { columns, rows };
}
